import { useTranslation } from "react-i18next";
import React, { useState } from 'react';
import { MetricChart } from './MetricChart';
import { ComparisonSelector, ComparisonType } from './ComparisonSelector';
import { DownloadIcon } from 'lucide-react';
interface KeyMetric {
  id: string;
  title: string;
  value: string;
  change: string;
  tooltipText?: string;
}
interface KeyMetricsSectionProps {
  title: string;
  metrics: KeyMetric[];
  selectedMetricId: string;
  onMetricSelect: (id: string) => void;
  chartData: any[];
  previousChartData: any[];
  dataKey: string;
  xAxisKey?: string;
  yAxisFormatter?: (value: any) => string;
  tooltipValueFormatter?: (value: any) => string;
  tooltipLabel?: string;
  dateFormatter: (date: string) => string;
  updateTime: string;
  chartColor?: string;
  comparison: ComparisonType;
  onComparisonChange: (comparison: ComparisonType) => void;
  dateRange: {
    start: Date;
    end: Date;
  };
  chartType?: 'line' | 'bar';
  yAxisDomain?: [number, number] | undefined;
  onDownload?: () => void;
}
export function KeyMetricsSection({
  title,
  metrics,
  selectedMetricId,
  onMetricSelect,
  chartData,
  previousChartData,
  dataKey,
  xAxisKey = 'date',
  yAxisFormatter,
  tooltipValueFormatter,
  tooltipLabel,
  dateFormatter,
  updateTime,
  chartColor = '#D4AF37',
  comparison,
  onComparisonChange,
  dateRange,
  chartType = 'line',
  yAxisDomain,
  onDownload
}: KeyMetricsSectionProps) {
  const {
    t
  } = useTranslation("analytics");
  const selectedMetric = metrics.find(m => m.id === selectedMetricId);
  return <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1921">
      <div className="flex items-center justify-between mb-4" data-id="element-1922">
        <div className="flex items-center gap-4" data-id="element-1923">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-1924">{title}</h2>
          <ComparisonSelector value={comparison} onChange={onComparisonChange} data-id="element-1925" />
          
        </div>
        <div className="flex items-center gap-2" data-id="element-1926">
          <button onClick={onDownload} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1927">
            
            <DownloadIcon size={14} data-id="element-1928" />
            {t("components.CardPaymentsBreakdown.download")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-1929">
        {metrics.map(metric => <MetricCard key={metric.id} title={metric.title} value={metric.value} change={metric.change} tooltipText={metric.tooltipText} isSelected={selectedMetricId === metric.id} onClick={() => onMetricSelect(metric.id)} data-id="element-1930" />)}
      </div>

      {/* Use MetricChart component */}
      <MetricChart title={selectedMetric?.title || ''} tooltipText={selectedMetric?.tooltipText} currentValue={selectedMetric?.value || ''} growthPercent={parseFloat(selectedMetric?.change || '0')} previousValue={selectedMetric?.value || ''} data={chartData} previousData={comparison !== 'none' ? previousChartData : undefined} dataKey={dataKey} xAxisKey={xAxisKey} yAxisFormatter={yAxisFormatter} tooltipValueFormatter={tooltipValueFormatter} tooltipLabel={tooltipLabel} dateFormatter={dateFormatter} updateTime={updateTime} chartColor={chartColor} yAxisDomain={yAxisDomain} data-id="element-1931" />
      

      {/* Legend */}
      <div className="flex items-center gap-4 mt-4" data-id="element-1932">
        <div className="flex items-center gap-2" data-id="element-1933">
          <div className="w-3 h-3 rounded-full" style={{
          backgroundColor: chartColor
        }} data-id="element-1934">
          </div>
          <span className="text-xs text-gray-600" data-id="element-1935">{t("components.KeyMetricsSection.current_period")}</span>
        </div>
        {comparison !== 'none' && <div className="flex items-center gap-2" data-id="element-1936">
            <div className="w-3 h-3 rounded-full bg-gray-400" data-id="element-1937"></div>
            <span className="text-xs text-gray-600" data-id="element-1938">
              {t("components.AuthenticationTab.previous_period")}
              {comparison === 'previous_period' && (() => {
            const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
            const prevStart = new Date(dateRange.start);
            prevStart.setDate(prevStart.getDate() - days);
            const prevEnd = new Date(dateRange.end);
            prevEnd.setDate(prevEnd.getDate() - days);
            const formatDate = (date: Date) => {
              return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              });
            };
            return ` (${formatDate(prevStart)} - ${formatDate(prevEnd)})`;
          })()}
            </span>
          </div>}
      </div>
    </div>;
}
function MetricCard({
  title,
  value,
  change,
  tooltipText,
  isSelected,
  onClick
}: any) {
  const [showTooltip, setShowTooltip] = useState(false);
  const isNegative = change.startsWith('-');
  return <button onClick={onClick} className={`text-left p-4 rounded-lg border-2 transition-all ${isSelected ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1939">
      
      <div className="flex items-center gap-1 mb-2" data-id="element-1940">
        <span className="text-sm text-gray-600" data-id="element-1941">{title}</span>
        {tooltipText && <div className="relative" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} data-id="element-1942">
          
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" className="text-gray-400 cursor-help" data-id="element-1943">
            
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" data-id="element-1944" />
              <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" data-id="element-1945" />
            </svg>
            {showTooltip && <div className="absolute left-0 top-6 z-50 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg" data-id="element-1946">
                {tooltipText}
                <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 transform rotate-45" data-id="element-1947"></div>
              </div>}
          </div>}
      </div>
      <div className="text-2xl font-semibold text-gray-900 mb-1" data-id="element-1948">{value}</div>
      <div className={`text-sm ${isNegative ? 'text-red-600' : 'text-green-600'}`} data-id="element-1949">
        
        {change}
      </div>
    </button>;
}